import type {TicketingAttachment} from './types';
import type {AsyncThunk} from '@reduxjs/toolkit';

import {cn} from '@ds/helpers';

import AttachmentItem from './AttachmentItem';
import LinkifiedText from './LinkifiedText';

type DownloadAttachmentPayload = {
  id: string | number;
  vendor_id: string | number;
};

const formatMessageDate = (date: string): string => {
  const messageDate = new Date(date);
  const time = messageDate.toLocaleTimeString('fa-IR', {
    hour: '2-digit',
    minute: '2-digit'
  });
  const day = messageDate.toLocaleDateString('fa-IR');

  return `${time} - ${day}`;
};

type TicketingMessageProps = {
  title?: string;
  description?: string;
  createdAt?: string;
  attachments?: TicketingAttachment[] | null;
  vendorId: string | number;
  downloadAttachment: AsyncThunk<unknown, DownloadAttachmentPayload, object>;
  source?: 'vendor' | 'snapp';
};

const TicketingMessage: React.FC<TicketingMessageProps> = ({
  title,
  description,
  createdAt,
  attachments,
  vendorId,
  downloadAttachment,
  source = 'vendor'
}) => {
  const normalizedAttachments = Array.isArray(attachments) ? attachments : [];

  return (
    <article
      className={cn(
        'flex min-w-0 w-full flex-col gap-2.5 rounded-xl p-3',
        source === 'snapp' ? 'bg-orange-wash' : 'bg-gray-02'
      )}
    >
      {title ? <h3 className="text-body-xs text-gray-07">{title}</h3> : null}
      <p className="wrap-break-word whitespace-pre-line text-body-sm text-neutral-fg">
        <LinkifiedText text={description} />
      </p>

      {normalizedAttachments.length > 0 ? (
        <div className="flex flex-col gap-2.5">
          {normalizedAttachments.map((file) => (
            <AttachmentItem
              key={file.id}
              file={file}
              vendorId={vendorId}
              downloadAttachment={downloadAttachment}
            />
          ))}
        </div>
      ) : null}

      {createdAt ? (
        <time className="self-end text-body-xs text-gray-07">{formatMessageDate(createdAt)}</time>
      ) : null}
    </article>
  );
};

export default TicketingMessage;
